/**
 * What the reply area offers on a finished ticket (LIVE SR 10578, 9 Oct).
 *
 * The requester wrote "Not working" on a Fulfilled request, it stayed
 * Fulfilled and nobody was told. The server now says what a reply does
 * (`reply_effect`), and this decides what the page shows for it:
 *
 * - `reopen`: the comment box, with "Sending this will reopen your ticket and
 *   notify IT." (a Fulfilled request; a Resolved incident once Ivanti allows
 *   the reopen).
 * - `notify`: the comment box, with "IT will be told" (a Resolved incident,
 *   which Ivanti will not let the HelpDesk reopen).
 * - `report`: no comment box can help (Closed is final in Ivanti, or a
 *   finished ticket takes no reply), so "Still not working? Report a problem"
 *   opens a new incident that names this ticket.
 * - `none`: an ordinary open ticket, or a view that may not write.
 */

/** The fields this reads; `TicketDetail` satisfies it. */
export interface FinishedReplyInput {
  status: { code: string };
  permitted_operations?: string[] | null;
  reply_effect?: string | null;
  viewed_as_admin?: boolean | null;
  viewed_as_migrator?: boolean | null;
  viewed_by_link?: boolean | null;
  named_as?: string | null;
}

export type FinishedReplyMode = "reopen" | "notify" | "report" | "none";

/** Statuses that are done with, where a new problem needs a new ticket. */
const FINISHED = new Set(["RESOLVED", "FULFILLED", "CLOSED"]);

export function finishedReplyMode(ticket: FinishedReplyInput): FinishedReplyMode {
  // Someone else's ticket, read-only: nothing to offer.
  if (
    ticket.viewed_as_admin === true ||
    ticket.viewed_as_migrator === true ||
    ticket.viewed_by_link === true
  ) {
    return "none";
  }
  const named = ticket.named_as != null && ticket.named_as !== "";
  const canReply = (ticket.permitted_operations ?? []).includes("ADD_COMMENT");
  if (canReply && ticket.reply_effect === "REOPENS") {
    return "reopen";
  }
  if (canReply && ticket.reply_effect === "NOTIFIES") {
    return "notify";
  }
  // Someone named on the request did not raise it: theirs to comment, not to re-raise.
  if (!canReply && !named && FINISHED.has(ticket.status.code)) {
    return "report";
  }
  return "none";
}

/** Where "Report a problem" goes: the incident form, following this ticket. */
export function reportProblemPath(ticketId: string): string {
  return `/tickets/new?kind=INCIDENT&follow_up=${encodeURIComponent(ticketId)}`;
}
